import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import GridData from 'src/components/GridData/GridData';
import { Dropdown } from 'src/UIKit';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { getFullDate } from 'src/helpers/date';
import { useAdminSignupsViewController, SignupsTimePeriod } from '../../controllers';
import { AdminSignupListItem, SignupStatus, SignupStepName } from '../../types';
import { SignupConversionSummary } from '../SignupConversionSummary';

const STATUS_OPTIONS = [
  { value: '', title: 'Any status' },
  { value: 'started', title: 'Started' },
  { value: 'verified', title: 'Verified' },
  { value: 'awaiting_payment', title: 'Awaiting payment' },
  { value: 'provisioned', title: 'Provisioned' },
  { value: 'completed', title: 'Completed' },
  { value: 'expired', title: 'Expired' },
];

const STEP_OPTIONS = [
  { value: '', title: 'Any step' },
  { value: 'plan', title: 'Plan' },
  { value: 'account', title: 'Account' },
  { value: 'verify_email', title: 'Verify email' },
  { value: 'company_info', title: 'Company info' },
  { value: 'phone_number', title: 'Phone number' },
  { value: 'payment', title: 'Payment' },
  { value: 'finalizing', title: 'Finalizing' },
  { value: 'registration', title: 'Registration' },
  { value: 'done', title: 'Done' },
];

const PERIOD_OPTIONS = [
  { value: 'last7days', title: 'Last 7 days' },
  { value: 'last30days', title: 'Last 30 days' },
  { value: 'last90days', title: 'Last 90 days' },
  { value: 'custom', title: 'Custom' },
];

export const AdminSignupsView = () => {
  const navigate = useNavigate();
  const {
    timePeriod,
    queryInput,
    changeQuery,
    startDate,
    endDate,
    filter,
    signups,
    count,
    conversion,
    isLoading,
    isExporting,
    applicationOptions,
    page,
    changePeriod,
    changeStartDate,
    changeEndDate,
    changeFilter,
    changeSort,
    changePage,
    changePageSize,
    exportSignups,
  } = useAdminSignupsViewController();

  return (
    <AdminViewWrapper>
      <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.signups} />}>
        {/*
          The layout hands this box a definite height, and it has to be handed on: `GridData` sizes its table
          from a measurement of its own container, so a parent whose height comes from its content grows a
          little on every measurement instead of settling. Flex `gap` rather than `Grid spacing`, which pays
          for its gutters with negative margins and overflows a full-height row by exactly one gutter.
        */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            height: '100%',
            overflowY: { xs: 'auto', lg: 'hidden' },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: '100%', lg: 340 },
              height: { lg: '100%' },
              minHeight: 0,
              overflowY: { lg: 'auto' },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack gap={2} pt={1} mb={2}>
                <Dropdown
                  label="Period"
                  value={timePeriod}
                  options={PERIOD_OPTIONS}
                  onChange={(_, value) => changePeriod(value as SignupsTimePeriod)}
                  margin="none"
                />
                <DatePicker
                  label="From"
                  value={startDate}
                  onChange={changeStartDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To"
                  value={endDate}
                  onChange={changeEndDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Stack>
            </LocalizationProvider>

            {filter.query.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                The table is searching every signup; the summary below stays on the period.
              </Typography>
            )}

            <SignupConversionSummary conversion={conversion} isLoading={isLoading} />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 480, lg: 0 },
              height: { lg: '100%' },
              // `GridData` asks for the whole height of its parent and then draws a border outside it, so on
              // a parent with a definite height it stands two pixels taller than the room it was given and
              // loses its bottom edge under the clip. Counting the border inside those hundred per cent is
              // the local half of the fix; the component itself does this to every screen that bounds it.
              '& > *': { boxSizing: 'border-box' },
            }}
          >
            <GridData
              header={
                <TableHeader
                  searchValue={queryInput}
                  searchPlaceholder="Email, number, company or funnel id"
                  onSearchChange={(_: ChangeEvent<HTMLInputElement>, query: string) => changeQuery(query)}
                  filters={
                    <>
                      <TableHeaderItem>
                        <Dropdown
                          label="Status"
                          value={filter.statuses[0] ?? ''}
                          options={STATUS_OPTIONS}
                          onChange={(_, value) =>
                            changeFilter({ statuses: value ? [value as SignupStatus] : [] })
                          }
                          margin="none"
                        />
                      </TableHeaderItem>
                      <TableHeaderItem>
                        <Dropdown
                          label="Step"
                          value={filter.steps[0] ?? ''}
                          options={STEP_OPTIONS}
                          onChange={(_, value) =>
                            changeFilter({ steps: value ? [value as SignupStepName] : [] })
                          }
                          margin="none"
                        />
                      </TableHeaderItem>
                      <TableHeaderItem>
                        <Dropdown
                          label="Application"
                          value={filter.applicationIds[0] ?? ''}
                          options={[{ value: '', title: 'Any application' }, ...applicationOptions]}
                          onChange={(_, value) =>
                            changeFilter({ applicationIds: value ? [value] : [] })
                          }
                          margin="none"
                        />
                      </TableHeaderItem>
                    </>
                  }
                  actions={
                    <LoadingButton
                      variant="contained"
                      loading={isExporting}
                      onClick={exportSignups}
                      sx={TABLE_HEADER_ACTION_SX}
                    >
                      Export
                    </LoadingButton>
                  }
                />
              }
              isLoading={isLoading}
              rows={signups}
              onRowClick={r => navigate(`/admin/signups/${r.row.id}`)}
              columns={[
                {
                  field: 'email',
                  headerName: 'Contact',
                  renderCell: r => (
                    <Stack>
                      {/* The only name a funnel has before its email is confirmed and a company exists. */}
                      <Typography variant="body2" noWrap>
                        {r.row.email ?? 'no email yet'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" noWrap>
                        {r.row.companyName ?? 'no company yet'}
                      </Typography>
                    </Stack>
                  ),
                  minWidth: 200,
                  sortable: false,
                  flex: 1.5,
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  renderCell: r => <Chip label={r.row.status} size="small" color={statusColor(r.row.status)} />,
                  minWidth: 130,
                  sortable: false,
                  flex: 1,
                },
                { field: 'step', headerName: 'Step', minWidth: 120, sortable: false, flex: 1 },
                { field: 'source', headerName: 'Source', minWidth: 100, sortable: false, flex: 1 },
                { field: 'planName', headerName: 'Plan', minWidth: 100, sortable: false, flex: 1 },
                {
                  field: 'selectedNumber',
                  headerName: 'Number',
                  minWidth: 130,
                  sortable: false,
                  flex: 1,
                },
                {
                  field: 'dateCreated',
                  headerName: 'Started',
                  renderCell: r => getFullDate(r.row.dateCreated),
                  minWidth: 150,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'dateLastActivity',
                  headerName: 'Last activity',
                  renderCell: r => getFullDate(r.row.dateLastActivity),
                  minWidth: 150,
                  sortable: true,
                  flex: 1,
                },
              ]}
              sorting={{
                value: filter.sort,
                onChange: changeSort,
              }}
              pagination={{
                page: page,
                pageSize: filter.limit,
                totalCount: count,
                onChange: model => {
                  if (model.pageSize != filter.limit) changePageSize(model.pageSize);
                  else changePage(model.page);
                },
              }}
            />
          </Box>
        </Box>
      </AdminLayoutView>
    </AdminViewWrapper>
  );
};

function statusColor(status: AdminSignupListItem['status']) {
  if (status == 'completed') return 'success' as const;
  if (status == 'expired') return 'default' as const;
  if (status == 'provisioned') return 'warning' as const;
  return 'primary' as const;
}
